// An error no component catches unmounts the whole page, and src/client.tsx
// puts the server's HTML back (keep-server-html.ts). The commonest cause was a
// page cut short, as Googlebot cuts every page at 2 MB, so the React payload
// arrived incomplete. Pages are under 2 MB now (docs/SEO.md, rule 1), but the
// handler only wrote to the console, where nobody sees it: nothing said how
// often readers or crawlers still met a blanked page.
//
// So a sample of these errors goes to Sentry, tagged so they can be counted
// apart from the rest: `page_blanked`, and `reader` (crawler or browser, from
// the user agent). A tenth keeps the volume small if something blanks many
// pages at once; multiply a count by ten for the true number.

import { captureReactException } from "@sentry/react";
import type { ErrorInfo } from "react";

/** The share of blanked pages reported to Sentry. */
export const BLANKED_PAGE_SAMPLE_RATE = 0.1;

/** User agents of search engine crawlers and the renderers that test pages for them. */
const CRAWLER = /bot\b|crawler|spider|Google-InspectionTool|Chrome-Lighthouse/i;

export interface BlankedPageReportOptions {
  /** Sentry's React capture; the default sends to the client set up in src/client.tsx. */
  capture?: typeof captureReactException;
  /** A number in [0, 1), as Math.random gives. */
  random?: () => number;
  userAgent?: string;
}

/**
 * Report an error that unmounted the page, for one page view in ten.
 * Returns Sentry's event id, or undefined when this view is not sampled.
 */
export function reportBlankedPage(
  error: unknown,
  errorInfo: ErrorInfo,
  { capture = captureReactException, random = Math.random, userAgent = globalThis.navigator?.userAgent ?? "" }: BlankedPageReportOptions = {}
): string | undefined {
  if (random() >= BLANKED_PAGE_SAMPLE_RATE) return undefined;
  return capture(error, errorInfo, {
    level: "error",
    tags: { page_blanked: "true", reader: CRAWLER.test(userAgent) ? "crawler" : "browser" },
    extra: { sampleRate: BLANKED_PAGE_SAMPLE_RATE },
  });
}
